(() => {
  'use strict';

  const COLLECTIONS_MOBILE = [
    'Mechanical Models',
    'Wooden Puzzles',
    'Coin Banks',
    'Mini Models',
    'Pocket Cars',
    'Wall Décor & Maps',
    'Wooden Flowers',
    'Board Games'
  ];


  // Resize only Shopify-hosted files; preserve version and other URL parameters.
  function categoryImageUrl(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      if ((url.hostname === window.location.hostname && url.pathname.startsWith('/cdn/shop/')) ||
          (url.hostname === 'cdn.shopify.com' && url.pathname.startsWith('/s/files/'))) {
        url.searchParams.set('width', String(width));
        return url.href;
      }
    } catch (_) {}
    return src;
  }
  const collectionCache = new Map();
  const previewImageCache = new Map();

  function normalize(value) {
    return (value || '')
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function absoluteUrl(url) {
    try { return new URL(url, window.location.origin).href; }
    catch (_) { return url; }
  }

  function cleanText(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  function truncate(value, max = 170) {
    value = cleanText(value);
    if (value.length <= max) return value;
    return value.slice(0, max - 1).replace(/[\s,;:.!?-]+$/, '') + '…';
  }

  // Load collection previews before the menu is opened so hover swaps do not
  // briefly show a generic image before the correct collection image arrives.
  function preloadPreviewImage(src) {
    if (!src) return Promise.resolve('');
    src = categoryImageUrl(src, 800);
    if (previewImageCache.has(src)) return previewImageCache.get(src);

    const promise = new Promise(resolve => {
      const preload = new Image();
      preload.onload = () => resolve(src);
      preload.onerror = () => resolve('');
      preload.src = src;
    });
    previewImageCache.set(src, promise);
    return promise;
  }

  async function searchCollection(title) {
    const key = normalize(title);
    const cacheKey = 'search:' + key;
    if (collectionCache.has(cacheKey)) return collectionCache.get(cacheKey);

    const promise = (async () => {
      const endpoint = root() + 'search/suggest.json?q=' + encodeURIComponent(title)
        + '&resources[type]=collection&resources[limit]=10';
      const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Predictive search returned ' + response.status);
      const data = await response.json();
      const results = data && data.resources && data.resources.results;
      const collections = results && results.collections ? results.collections : [];
      const exact = collections.find(item => normalize(item.title) === key) || (collections.length === 1 ? collections[0] : null);
      if (!exact || !exact.url) throw new Error('Collection not found: ' + title);
      return { title: exact.title || title, url: exact.url };
    })().catch(error => {
      console.warn('[EWA v6] Could not resolve collection:', title, error);
      return null;
    });

    collectionCache.set(cacheKey, promise);
    return promise;
  }

  async function collectionMeta(title) {
    const key = normalize(title);
    const cacheKey = 'meta:' + key;
    if (collectionCache.has(cacheKey)) return collectionCache.get(cacheKey);

    const promise = (async () => {
      const base = await searchCollection(title);
      if (!base) return null;

      let result = { ...base, description: '', image: '' };
      try {
        const response = await fetch(base.url, { headers: { Accept: 'text/html' } });
        if (!response.ok) return result;
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const descriptionNode =
          doc.querySelector('.hdt-collection-description') ||
          doc.querySelector('.hdt-collection-description-short') ||
          doc.querySelector('[data-collection-description]');

        const metaDescription =
          doc.querySelector('meta[name="description"]')?.content ||
          doc.querySelector('meta[property="og:description"]')?.content || '';

        const image =
          doc.querySelector('meta[property="og:image:secure_url"]')?.content ||
          doc.querySelector('meta[property="og:image"]')?.content || '';

        result.description = truncate(descriptionNode ? descriptionNode.textContent : metaDescription);
        result.image = image ? absoluteUrl(image) : '';
      } catch (error) {
        console.warn('[EWA v6] Could not load collection content:', title, error);
      }
      return result;
    })();

    collectionCache.set(cacheKey, promise);
    return promise;
  }

  function setResolvedLink(link, data) {
    if (!link || !data || !data.url) return;
    link.href = data.url;
    link.dataset.collectionResolved = '1';
  }

  function enhanceDesktopMega(menu) {
    if (!menu || menu.dataset.ewaV6Ready === '1') return;
    menu.dataset.ewaV6Ready = '1';

    const image = menu.querySelector('.ewa-menu-v4__image');
    const titleNode = menu.querySelector('.ewa-menu-v4__feature-title');
    const textNode = menu.querySelector('.ewa-menu-v4__feature-text');
    const button = menu.querySelector('.ewa-menu-v4__button');
    const links = [...menu.querySelectorAll('[data-preview]')];
    if (!image || !titleNode || !textNode || !button || !links.length) return;

    let hoverToken = 0;

    function prefetchCollection(link) {
      const requestedTitle = link.dataset.title || cleanText(link.textContent);
      searchCollection(requestedTitle).then(data => setResolvedLink(link, data));
      collectionMeta(requestedTitle).then(meta => {
        if (!meta) return;
        setResolvedLink(link, meta);
        preloadPreviewImage(meta.image);
      });
    }

    function showFallback(link) {
      links.forEach(a => a.classList.remove('is-active'));
      link.classList.add('is-active');
      titleNode.textContent = link.dataset.title || cleanText(link.textContent);
      textNode.textContent = link.dataset.text || '';
      if (link.dataset.image) {
        image.src = categoryImageUrl(link.dataset.image, 800);
        image.alt = titleNode.textContent;
      }
    }

    async function showCollection(link) {
      const token = ++hoverToken;
      showFallback(link);
      const requestedTitle = link.dataset.title || cleanText(link.textContent);
      const meta = await collectionMeta(requestedTitle);
      if (!meta) return;
      setResolvedLink(link, meta);
      if (token !== hoverToken) return;

      titleNode.textContent = meta.title || requestedTitle;
      if (meta.description) textNode.textContent = meta.description;
      if (meta.image) {
        const loadedImage = await preloadPreviewImage(meta.image);
        if (token !== hoverToken) return;
        if (loadedImage) {
          image.src = loadedImage;
          image.alt = meta.title || requestedTitle;
        }
      }
      if (meta.url) button.href = meta.url;
    }

    links.forEach(link => {
      const requestedTitle = link.dataset.title || cleanText(link.textContent);
      prefetchCollection(link);

      link.addEventListener('mouseenter', () => showCollection(link));
      link.addEventListener('focus', () => showCollection(link));
      link.addEventListener('click', event => {
        if (link.dataset.collectionResolved === '1') return;
        event.preventDefault();
        searchCollection(requestedTitle).then(data => {
          if (data?.url) window.location.assign(data.url);
        });
      });
    });

    const active = links.find(link => link.classList.contains('is-active')) || links[0];
    if (active) showCollection(active);
  }

  function mobileCollectionItem(title) {
    const li = document.createElement('li');
    li.className = 'hdt-menu-item hdt-flex hdt-justify-center hdt-flex-col ewa-mobile-collection-item';

    const a = document.createElement('a');
    a.className = 'hdt-menu-link hdt-flex hdt-align-center hdt-justify-between hdt-relative hdt-text-base hdt-font-normal';
    a.href = root() + 'search?q=' + encodeURIComponent(title);
    a.dataset.ewaMobileCollection = title;

    const span = document.createElement('span');
    span.className = 'hdt-menu-link-text hdt-flex hdt-align-center hdt-justify-center';
    const text = document.createElement('span');
    text.className = 'hdt-block hdt-line-clamp';
    text.textContent = title;
    span.appendChild(text);
    a.appendChild(span);
    li.appendChild(a);

    if (title === 'All Products') {
      a.href = root() + 'collections/all';
      a.dataset.collectionResolved = '1';
      return li;
    }

    searchCollection(title).then(data => setResolvedLink(a, data));
    a.addEventListener('click', event => {
      if (a.dataset.collectionResolved === '1') return;
      event.preventDefault();
      searchCollection(title).then(data => {
        if (data?.url) window.location.assign(data.url);
      });
    });
    return li;
  }

  function simplifyMobileShop() {
    document.querySelectorAll('#hdt-nav-ul-mb').forEach(nav => {
      if (nav.dataset.ewaV6MobileReady === '1') return;
      const topItems = [...nav.children].filter(el => el.matches('li'));
      const shopItem = topItems.find(li => {
        const topControl = li.querySelector(':scope > hdt-accordion > details > summary, :scope > a');
        if (!topControl) return false;
        const text = topControl.querySelector('.hdt-menu-link-text')?.textContent || topControl.textContent;
        return normalize(text).replace(/\s+/g, ' ') === 'shop';
      });
      if (!shopItem) return;

      const submenu = shopItem.querySelector(':scope > hdt-accordion > details > ul.hdt-mb-sub-menu-lever-1');
      if (!submenu) return;

      submenu.innerHTML = '';
      submenu.appendChild(mobileCollectionItem('All Products'));
      COLLECTIONS_MOBILE.forEach(title => submenu.appendChild(mobileCollectionItem(title)));
      nav.dataset.ewaV6MobileReady = '1';
      shopItem.dataset.ewaMobileShop = 'collections-only';
    });
  }

  function init() {
    document.querySelectorAll('[data-ewa-mega-v6]').forEach(enhanceDesktopMega);
    simplifyMobileShop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Ecomus can lazy-load the mobile navigation. Re-run only when new nodes appear.
  const observer = new MutationObserver(() => init());
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
